import * as React from 'react';
import { useState, useMemo, useEffect } from 'react';
import { Box, ToggleButtonGroup, ToggleButton, Tooltip as MuiTooltip, Typography, useTheme } from '@mui/material';
import {
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    BarChart,
    Bar,
    CartesianGrid,
    ReferenceLine
} from 'recharts';

import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Import time icon
import TrendingUpIcon from '@mui/icons-material/TrendingUp'; // Import performance icon
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import { Fund } from '../../types/types';

// Types for chart data
interface PerformanceDataPoint {
    date: number;
    value: number;
    dateStr: string;
}

type TimeRangeType = 'YTD' | '1Y' | 'ALL';
type ChartType = 'area' | 'bar';

// Generates daily data for testing
const generateHistoricalData = (days = 365): PerformanceDataPoint[] => {
    const data = [];
    const today = new Date();
    let currentValue = 40;

    for (let i = days; i >= 0; i--) {
        const currentDate = new Date();
        currentDate.setDate(today.getDate() - i);

        const change = (Math.random() * 0.8 - 0.3) * (Math.random() < 0.7 ? 1 : 2);
        currentValue = Math.max(10, currentValue + change);

        data.push({
            date: currentDate.getTime(),
            value: parseFloat(currentValue.toFixed(2)),
            dateStr: `${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()}`
        });
    }

    return data;
};

const generateVolumeData = (days = 365): PerformanceDataPoint[] => {
    const data: PerformanceDataPoint[] = [];
    const today = new Date();
    let currentValue = 0;

    for (let i = days; i >= 0; i--) {
        const currentDate = new Date();
        currentDate.setDate(today.getDate() - i);

        const date = currentDate.getTime();
        const dateStr = `${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()}`;

        const random = Math.random();
        if (random < 0.1) {
            // 10% chance to decrease
            const delta = Math.floor(Math.random() * (25000 - 5000 + 1)) + 5000;
            currentValue = Math.max(0, currentValue - delta);
        } else if (random < 0.4) {
            // 30% chance to increase
            const delta = Math.floor(Math.random() * (50000 - 5000 + 1)) + 5000;
            currentValue += delta;
        }
        // else: no change (60% of the time)

        data.push({
            date,
            dateStr,
            value: currentValue
        });
    }

    return data;
};


// Filter data based on selected time range
const getFilteredData = (fullData: PerformanceDataPoint[], range: TimeRangeType): PerformanceDataPoint[] => {
    const today = new Date();

    switch (range) {
        case 'YTD':
            return fullData.filter(item => item.date >= new Date(today.getFullYear(), 0, 1).getTime());
        case '1Y':
            return fullData.filter(item => item.date >= new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()).getTime());
        case 'ALL':
        default:
            return fullData;
    }
};

const formatTickLabel = (tick: number, dataLength: number): string => {
    const date = new Date(tick);

    if (dataLength < 14) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); // "Jan 5"
    } else if (dataLength < 90) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); // "Jan 5"
    } else if (dataLength < 630) {
        return date.toLocaleDateString('en-US', { month: 'short' }); // "Jan"
    } else {
        return date.getFullYear().toString(); // "2022"
    }
};

const filterTicks = (ticks: number[], timeRange: TimeRangeType) => {
    const numDays = ticks.length;

    if (numDays < 14) {
        // Show every day
        return ticks;
    } else if (numDays < 90) {
        // Show first day of each week (Monday)
        return ticks.filter((tick) => {
            const date = new Date(tick);
            return date.getDay() === 1; // Monday
        });
    } else if (numDays < 630) {
        // Show only the first of each month
        return ticks.filter((tick) => {
            const date = new Date(tick);
            return date.getDate() === 1;
        });
    } else {
        // Show only January 1st of each year
        return ticks.filter((tick) => {
            const date = new Date(tick);
            return date.getMonth() === 0 && date.getDate() === 1;
        });
    }
};

interface FundChartProps {
    fund: Fund;
}

export const FundChart = ({ fund }: FundChartProps) => {
    const [timeRange, setTimeRange] = useState<TimeRangeType>('YTD');
    const [chartType, setChartType] = useState<ChartType>('area');
    const theme = useTheme();
    const themePrimaryColor = theme?.palette?.primary?.main || '#1976d2';

    useEffect(() => {
        // Initialize chart type based on corporation
        if (fund.corporation === 'Genossenschaft') {
            setChartType('bar');
        } else {
            setChartType('area');
        }
    }, [fund.corporation]);

    const fullHistoricalData = useMemo(() => generateHistoricalData(1095), []);
    const displayData = useMemo(
        () => getFilteredData(fullHistoricalData, timeRange),
        [fullHistoricalData, timeRange]
    );

    const fullVolumeData = useMemo(() => generateVolumeData(1095), []);
    const displayVolumeData = useMemo(
        () => getFilteredData(fullVolumeData, timeRange),
        [fullVolumeData, timeRange]
    );

    const startValue = displayData[0]?.value || 0;
    const currentValue = displayData[displayData.length - 1]?.value || 0;
    const percentChange = ((currentValue - startValue) / startValue * 100).toFixed(2);
    const isPositive = parseFloat(percentChange) >= 0;
    const baselineValue = startValue;

    const displayedDays = displayData.length;

    const handleTimeRangeChange = (event: React.MouseEvent<HTMLElement>, newRange: TimeRangeType | null) => {
        event.preventDefault();
        if (newRange) setTimeRange(newRange);
    };

    const handleChartTypeChange = (event: React.MouseEvent<HTMLElement>, newType: ChartType | null) => {
        event.preventDefault();
        if (newType) setChartType(newType);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '8px 0 0 0' }}>
            {/* Top Section with Metrics and Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Chart Type Buttons */}
                    <ToggleButtonGroup
                        size="small"
                        value={chartType}
                        exclusive
                        onChange={handleChartTypeChange}
                        aria-label="chart type"
                    >
                        <ToggleButton value="area" aria-label="performance" sx={{ height: 28, px: 1 }}>
                            <MuiTooltip title="Performance" arrow>
                                <ShowChartIcon fontSize="small" />
                            </MuiTooltip>
                        </ToggleButton>
                        <ToggleButton value="bar" aria-label="volume" sx={{ height: 28, px: 1 }}>
                            <MuiTooltip title="Volume" arrow>
                                <BarChartIcon fontSize="small" />
                            </MuiTooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Time Range Buttons */}
                    <ToggleButtonGroup
                        size="small"
                        value={timeRange}
                        exclusive
                        onChange={handleTimeRangeChange}
                        aria-label="time range"
                    >
                        <ToggleButton value="YTD" aria-label="ytd" sx={{ height: 28, px: 1 }}>YTD</ToggleButton>
                        <ToggleButton value="1Y" aria-label="1 year" sx={{ height: 28, px: 1 }}>1Y</ToggleButton>
                        <ToggleButton value="ALL" aria-label="all time" sx={{ height: 28, px: 1 }}>ALL</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {/* Right: Days Count & Performance */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Displayed Days */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">{displayedDays}d</Typography>
                    </Box>

                    {/* Fund Performance */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TrendingUpIcon fontSize="small" sx={{ color: isPositive ? 'success.main' : 'error.main' }} />
                        <Typography variant="body2" sx={{ color: isPositive ? 'success.main' : 'error.main' }}>
                            {isPositive ? '+' : ''}{percentChange}%
                        </Typography>
                    </Box>
                </Box>
            </Box>


            {/* Chart Section */}
            <Box sx={{ flex: 1, width: '100%', height: "100%", paddingBottom: 0, cursor: 'pointer' }}>
                {chartType === 'bar' ? (
                    <BarChartView
                        data={displayVolumeData}
                        color={themePrimaryColor}
                        formatTick={formatTickLabel}
                        filterTicksFn={filterTicks}
                        timeRange={timeRange}
                    />
                ) : (
                    <AreaChartView
                        data={displayData}
                        color={themePrimaryColor}
                        formatTick={formatTickLabel}
                        filterTicksFn={filterTicks}
                        timeRange={timeRange}
                    />
                )}
            </Box>
        </Box >
    );
};



// Chart Subcomponents
const AreaChartView = ({
    data,
    color,
    formatTick,
    filterTicksFn,
    timeRange
}: {
    data: PerformanceDataPoint[];
    color: string;
    formatTick: (tick: number, len: number) => string;
    filterTicksFn: (ticks: number[], range: TimeRangeType) => number[];
    timeRange: TimeRangeType;
}) => (
    <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
            <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.2} />
                </linearGradient>
            </defs>
            <XAxis
                dataKey="date"
                scale="time"
                type="number"
                padding={{ left: 0, right: 0 }}
                domain={['auto', 'auto']}
                tickFormatter={(tick) => formatTick(tick, data.length)}
                ticks={filterTicksFn(data.map(d => d.date), timeRange)}
                tick={{ fontSize: 10 }}
                tickMargin={10}
                axisLine={{ stroke: '#E0E0E0' }}
            />
            <YAxis
                domain={['dataMin', 'dataMax']}
                tick={false}
                axisLine={false}
                tickLine={false}
                width={0}
            />
            <Tooltip
                formatter={(value: number) => [`€${value.toFixed(2)}`, 'Value']}
                labelFormatter={(label: number) => new Date(label).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                })}
            />
            <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorValue)"
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
            />
        </AreaChart>
    </ResponsiveContainer>
);

const BarChartView = ({
    data,
    color,
    formatTick,
    filterTicksFn,
    timeRange
}: {
    data: PerformanceDataPoint[];
    color: string;
    formatTick: (tick: number, len: number) => string;
    filterTicksFn: (ticks: number[], range: TimeRangeType) => number[];
    timeRange: TimeRangeType;
}) => (
    <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
            {/* Gradient fill to match AreaChart */}
            <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.2} />
                </linearGradient>
            </defs>

            <XAxis
                dataKey="date"
                scale="time"
                type="number"
                domain={['auto', 'auto']}
                tickFormatter={(tick) => formatTick(tick, data.length)}
                ticks={filterTicksFn(data.map(d => d.date), timeRange)}
                tick={{ fontSize: 10 }}
                tickMargin={10}
                axisLine={{ stroke: '#E0E0E0' }}
            />

            <YAxis
                domain={['auto', 'auto']}
                tick={false}
                axisLine={false}
                tickLine={false}
                width={0}
            />

            <Tooltip
                formatter={(value: number) => [`€${value.toFixed(2)}`, 'Value']}
                labelFormatter={(label: number) => new Date(label).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                })}
            />

            <Bar
                dataKey="value"
                fill="url(#colorValue)"
                radius={[2, 2, 0, 0]}
            />
        </BarChart>
    </ResponsiveContainer>
);